import React, { FunctionComponent, useContext, useEffect, useRef, useState } from 'react'
import { AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps'
import useLocalStorageValue from '../../../../views/Map/RightSider/Options/hooks/useLocalStroageValue'
import { VehicleLocation, Vehicle } from 'server'
import { getVehicleDotColor, getVehicleIcon } from '../utils'
import { MapSettingsContext } from '../../../../contexts/MapSettingsContext'
import MapSettingsContextInterface from '../../../../contexts/MapSettingsContext/MapSettingsContextInterface'
import { green } from '@ant-design/colors'
import classes from '../styles/marker.module.css'
import useVehicleLocation from '../../../../hooks/data/useVehicleLocation'
import LatLngLiteral = google.maps.LatLngLiteral
import { useQuery } from '@tanstack/react-query'
import { useFeathersClient } from '@strac/react-client-feathers'
import { AimOutlined, BookOutlined, CloseOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { renderVehicleInfoWindow } from '../utils/infoWindows'
import { arrayToMap } from '../../../../utils/data'
import { useLoaderData, useMatch } from '@tanstack/react-router'
import MapClickOptionsContainer from '../../MapClickOptionsContainer/MapClickOptionsContainer'
import { AssettrakAccess } from 'server'
import { canSeeVehicleLocationHistory } from '../../../../utils/auth'
import useVehicleLocationStorage from '../../../../hooks/data/useVehicleLocationStorage'
import VehicleLocationTrail from '../../../VehicleLocationTrail/VehicleLocationTrail'

interface IProps {
  vehicle: Vehicle
  vehicleLocation: VehicleLocation
  clusterer: any
  assettrakAccess: AssettrakAccess
}

const bloodUnitSimIds: string[] = ['M01', 'M45', 'MOF1', 'MOF2', 'MOF3', 'MOF4', 'MOF5', 'ID=0730', 'ID=0733']

const AdvancedMarkerComponentReference: FunctionComponent<any> = (props: IProps) => {
  const { vehicle, clusterer, assettrakAccess } = props

  const match = useMatch({ strict: false })
  const { agencies, vehicleTypes, adsb } = useLoaderData({ from: match.routeId ?? '/' })

  const agencyMap = arrayToMap(agencies, 'id')
  const vehicleTypeMap = arrayToMap(vehicleTypes, 'id')
  const adsbMap = arrayToMap(adsb, 'tailNumber')

  const {
    setSelectedVehicleHistory,
    followedVehicleId,
    setFollowedVehicleId,
    setSelectedVehicleId,
    selectedVehicleId
  } = useContext(MapSettingsContext) as MapSettingsContextInterface

  const [markerMenuOpen, setMarkerMenuOpen] = useState(false)
  const [infoWindowOpen, setInfoWindowOpen] = useState(false)

  const labels = useLocalStorageValue('assettrak_labels')
  const iconType = useLocalStorageValue('assettrak_icon_type')
  const cluster = useLocalStorageValue('assettrak_cluster')

  const map = useMap()

  const client = useFeathersClient()

  const vehicleLocation = useVehicleLocation(props.vehicleLocation.vehicle_id)

  const storage = useVehicleLocationStorage(props.vehicleLocation.vehicle_id)

  const vehicleType = vehicleTypeMap.get(vehicle.vehicle_type_id)

  const followed = followedVehicleId === vehicle.vehicle_id

  const [recentlyFocused, setRecentlyFocused] = useState(false)

  /**
   * Update map for follow
   */
  useEffect(() => {
    if (followedVehicleId === vehicleLocation?.vehicle_id) {
      if (map && vehicleLocation) {
        map?.panTo(new google.maps.LatLng(vehicleLocation?.latitude, vehicleLocation?.longitude))
        // smoothPanTo(map, { lat: vehicleLocation?.latitude, lng: vehicleLocation?.longitude })
      }
    }
  }, [map, followedVehicleId, vehicleLocation])

  const marker = useRef(
    new google.maps.marker.AdvancedMarkerElement({
      position: { lat: props.vehicleLocation?.latitude, lng: props.vehicleLocation?.longitude },
      gmpClickable: true,
      zIndex: 1
    })
  )

  // ICON SETTINGS
  const { width, height, src, name } = getVehicleIcon(vehicle, vehicleLocation, vehicleType)
  const speed = vehicleLocation?.speed > 5
  const color = getVehicleDotColor(vehicleType)
  const isAdsb = adsbMap.has(vehicle.vehicle_id)
  const createMarker = () => {
    let iconElement: HTMLImageElement | HTMLDivElement = document.createElement('div')

    // Create an image element for your icon
    if (iconType === 'icons') {
      const image = document.createElement('img') as HTMLImageElement
      image.src = src
      image.style.width = width
      image.style.height = height
      iconElement.appendChild(image)
    } else {
      iconElement = document.createElement('div') as HTMLDivElement
      iconElement.style.width = '10px'
      iconElement.style.height = '10px'
      iconElement.style.backgroundColor = color
      iconElement.style.borderRadius = '50%'
    }

    iconElement.onmouseenter = () => {
      setInfoWindowOpen(true)
    }

    iconElement.onmouseleave = () => {
      setInfoWindowOpen(false)
    }

    iconElement.addEventListener('contextmenu', (event) => {
      setMarkerMenuOpen(true)
    })

    const isBloodUnit = bloodUnitSimIds.includes(vehicle.sim_id)
    const labelElement = document.createElement('div')
    labelElement.innerHTML = `${name}${isAdsb ? ' ▲' : ''}${isBloodUnit ? '🩸' : ''}`
    labelElement.style.position = 'relative'
    labelElement.style.display = 'inline-block'
    labelElement.style.verticalAlign = 'middle'
    labelElement.style.minWidth = '20px'
    labelElement.style.lineHeight = '11px'
    labelElement.style.padding = '0 6px 2px 6px'
    labelElement.style.fontSize = '11px'
    labelElement.style.textAlign = 'center'
    labelElement.style.whiteSpace = 'nowrap'
    labelElement.style.background = speed ? green[3] : '#fff'
    labelElement.style.color = 'black'
    labelElement.style.borderRadius = '10px'
    labelElement.style.zIndex = '2'
    labelElement.className = 'georama-regular'
    labelElement.style.boxShadow = '0 0 0 1px darkgrey'
    const containerElement = document.createElement('div')
    containerElement.style.display = 'flex'
    containerElement.style.flexDirection = 'column'
    containerElement.style.justifyContent = 'center'
    containerElement.style.alignItems = 'center'
    labels && containerElement.appendChild(labelElement)
    containerElement.appendChild(iconElement)
    containerElement.className = classes.noSelect
    return containerElement
  }
  marker.current.content = createMarker()
  marker.current.element.dataset.name = vehicle.unit_name || vehicle.name

  /**
   * Close marker menu on map click
   */
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null | undefined>(null)

  useEffect(() => {
    if (mapClickListenerRef.current) {
      mapClickListenerRef.current.remove() // Remove existing listener before adding a new one
    }

    mapClickListenerRef.current = map?.addListener('click', () => {
      setMarkerMenuOpen(false)
    })

    return () => {
      mapClickListenerRef.current?.remove()
    }
  }, [map])

  /**
   * Add marker to map or clusterer depending on 'cluster' setting
   */
  useEffect(() => {
    if (!marker.current.position) return
    const markerCurrent = marker.current
    if (cluster) {
      clusterer.addMarker(marker.current)
    } else if (!cluster) {
      marker.current.map = map
    }
    return () => {
      if (cluster) {
        clusterer.removeMarker(markerCurrent)
      } else {
        markerCurrent.map = null
      }
    }
  }, [clusterer, cluster, map])

  /**
   * Window listeners
   */
  useEffect(() => {
    const listener = () => {
      setRecentlyFocused(true)
      setTimeout(() => {
        setRecentlyFocused(false)
      }, 3000)
    }
    window.addEventListener('focus', listener)
    return () => {
      window.removeEventListener('focus', listener)
    }
  }, [])

  /**
   * Update the marker position when the vehicle location changes
   */
  useEffect(() => {
    if (!vehicleLocation) return
    const currentPos = marker.current.position as LatLngLiteral
    if (currentPos.lat !== vehicleLocation.latitude || currentPos.lng !== vehicleLocation.longitude) {
      // If window was recently focused, don't animate the marker
      if (recentlyFocused) {
        marker.current.position = { lat: vehicleLocation.latitude, lng: vehicleLocation.longitude }
      } else {
        animateMarker(marker.current, { lat: vehicleLocation.latitude, lng: vehicleLocation.longitude })
      }
    }
  }, [vehicleLocation, recentlyFocused])

  /**
   * Other listeners
   */
  /*
  useEffect(() => {
    const markerCurrent = marker.current
    const listener = () => {
      setSelectedVehicleId(vehicle.vehicle_id)
    }
    marker.current?.addEventListener('gmp-click', listener)
    return () => {
      markerCurrent.removeEventListener('gmp-click', listener)
    }
  }, [setSelectedVehicleHistory, vehicle, setSelectedVehicleId])
   */

  useEffect(() => {
    console.log('selectedVehicleId', selectedVehicleId)
  }, [selectedVehicleId])

  /**
   * Map listener
   * Close info windows on user action
   */
  useEffect(() => {
    // Add click listener
    const mapMouseOutListener = map?.addListener('mouseout', (e: any) => {
      setInfoWindowOpen(false)
    })
    const mapClickListener = map?.addListener('click', (e: any) => {
      setInfoWindowOpen(false)
    })
    const mapZoomListener = map?.addListener('zoom_changed', (e: any) => {
      setInfoWindowOpen(false)
    })
    const mapDragListener = map?.addListener('drag', (e: any) => {
      setInfoWindowOpen(false)
    })
    return () => {
      mapMouseOutListener?.remove()
      mapClickListener?.remove()
      mapZoomListener?.remove()
      mapDragListener?.remove()
    }
  }, [map])

  const agency = agencyMap.get(vehicle.agency_id)

  const { data: deviceType } = useQuery({
    queryKey: ['device-type', 'get', vehicle.device_type_id],
    queryFn: () => client.service('device-type').get(vehicle.device_type_id),
    enabled: followed
  })

  const handleCloseInfoWindow = () => {
    setInfoWindowOpen(false)
  }

  const showHistory = canSeeVehicleLocationHistory(assettrakAccess, vehicle)

  const following = followedVehicleId === vehicle.vehicle_id

  return (
    <>
      {selectedVehicleId === vehicle.vehicle_id || followedVehicleId === vehicle.vehicle_id ? (
        <VehicleLocationTrail
          followedVehicleId={followedVehicleId}
          selectedVehicleId={selectedVehicleId}
          vehicleLocations={storage}
        />
      ) : null}
      {followed || infoWindowOpen ? (
        <InfoWindow
          zIndex={4}
          anchor={marker.current}
          pixelOffset={[0, -10]}
          onCloseClick={handleCloseInfoWindow}
        >
          {renderVehicleInfoWindow(vehicle, vehicleLocation, agency, vehicleType, deviceType)}
        </InfoWindow>
      ) : null}
      {markerMenuOpen ? (
        <AdvancedMarker
          style={{
            position: 'absolute',
            right: '-120px',
            top: '-40px',
            width: '150px'
          }}
          zIndex={4}
          position={marker.current.position}
        >
          <MapClickOptionsContainer>
            <Button
              block={true}
              onClick={() => {
                if (following) {
                  setFollowedVehicleId(undefined)
                } else {
                  setFollowedVehicleId(vehicle.vehicle_id)
                  const latLng = new google.maps.LatLng(vehicleLocation?.latitude, vehicleLocation?.longitude)
                  map?.panTo(latLng)
                  const zoom = map?.getZoom()
                  if (zoom) {
                    if (zoom < 15) {
                      map?.setZoom(15)
                    }
                  }
                }
              }}
              icon={following ? <CloseOutlined /> : <AimOutlined />}
              type={'primary'}
              size={'small'}
              danger={following}
            >
              {following ? 'Unfollow' : 'Follow'}
            </Button>
            {showHistory ? (
              <Button
                block={true}
                ghost={true}
                icon={<BookOutlined />}
                type={'primary'}
                size={'small'}
                onClick={() => setSelectedVehicleHistory(vehicle.vehicle_id)}
              >
                History
              </Button>
            ) : null}
          </MapClickOptionsContainer>
        </AdvancedMarker>
      ) : null}
    </>
  )
}

export default AdvancedMarkerComponentReference

const animateMarker = (
  marker: google.maps.marker.AdvancedMarkerElement,
  newPosition: {
    lat: number
    lng: number
  },
  duration = 5000
) => {
  const start = performance.now()
  const startPos: LatLngLiteral | null | undefined = marker.position as LatLngLiteral
  const latDelta = newPosition.lat - startPos.lat
  const lngDelta = newPosition.lng - startPos.lng

  function step(timestamp) {
    if (!(startPos?.lat && startPos?.lng)) return
    const progress = Math.min((timestamp - start) / duration, 1)
    const lat = startPos?.lat + latDelta * progress
    const lng = startPos?.lng + lngDelta * progress
    marker.position = new google.maps.LatLng(lat, lng)
    if (progress < 1) {
      requestAnimationFrame(step)
    }
  }

  try {
    requestAnimationFrame(step)
  } catch (e) {
    console.error(e)
    marker.position = { lat: newPosition?.lat, lng: newPosition?.lng }
  }
}
